(() => {
  const ALLOWED_TEMPLATES = new Set([
    'collection-page-1',
    'collection-page-2',
    'collection-page-3',
    'harry-potter-lincensed',
    'storm-trooper-licensed',
    'licensed-landing-pages',
    'christmas_landing_page',
  ]);
  const AJAX_BUTTON_MIN_LOADING_MS = 550;
  const AJAX_BUTTON_ADDED_MS = 45000;
  const CART_SETTLE_MS = 180;
  const TRACK_END_PERCENT = 96;
  const CART_FORM_SELECTOR = [
    'form[data-product-form]',
    'form[data-type="add-to-cart-form"]',
    'form[data-gift-form]',
    'form[action*="/cart/add"]',
  ].join(',');

  const state = {
    submittingForms: new WeakSet(),
    rewardRefreshId: 0,
  };

  const getExperience = () =>
    document.querySelector('collection-ajax-experience[data-cardology-collection-template]');
  const getBar = () => document.querySelector('[data-collection-aov-motivator]');
  const getTemplateSuffix = () => getExperience()?.dataset.cardologyCollectionTemplate || '';
  const isTargetCollectionPage = () =>
    ALLOWED_TEMPLATES.has(getTemplateSuffix()) && Boolean(getBar());

  const formatMoney = (pence, currency = 'GBP') => {
    const amount = Math.max(0, Number(pence) || 0) / 100;

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
    }
  };

  const applyTemplate = (template, values = {}) => {
    let output = template || '';

    Object.entries(values).forEach(([key, value]) => {
      output = output.replaceAll(`{${key}}`, value);
    });

    return output;
  };

  const readThreshold = (bar, datasetKey, fallback) => {
    if (Object.prototype.hasOwnProperty.call(bar.dataset, datasetKey)) {
      const value = Number(bar.dataset[datasetKey]);
      return Number.isFinite(value) ? value : fallback;
    }

    return fallback;
  };

  const getRewards = (bar) =>
    [
      {
        key: 'shipping',
        threshold: readThreshold(bar, 'freeShippingThreshold', 2000),
        label: bar.dataset.freeShippingLabel || 'FREE delivery',
        unlockedTitle: bar.dataset.freeShippingUnlockedTitle || 'FREE delivery unlocked!',
        icon: bar.dataset.freeShippingIcon || '',
      },
      {
        key: 'gift1',
        threshold: Object.prototype.hasOwnProperty.call(bar.dataset, 'freeGift1Threshold')
          ? readThreshold(bar, 'freeGift1Threshold', 3000)
          : readThreshold(bar, 'freeCardThreshold', 3000),
        label: bar.dataset.freeGift1Label || 'your first FREE gift',
        unlockedTitle:
          bar.dataset.freeGift1UnlockedTitle || 'Your first FREE gift is unlocked!',
        icon: bar.dataset.freeGift1Icon || '',
      },
      {
        key: 'gift2',
        threshold: readThreshold(bar, 'freeGift2Threshold', 6000),
        label: bar.dataset.freeGift2Label || 'your second FREE gift',
        unlockedTitle:
          bar.dataset.freeGift2UnlockedTitle || 'Your second FREE gift is unlocked!',
        icon: bar.dataset.freeGift2Icon || '',
      },
    ]
      .filter((reward) => reward.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);

  const getMilestonePositions = (rewards) => {
    if (!rewards.length) return [];

    const highestThreshold = rewards[rewards.length - 1].threshold;
    if (highestThreshold <= 0) return [];

    return rewards.map((reward) =>
      Math.min(
        TRACK_END_PERCENT,
        Math.max(0, (reward.threshold / highestThreshold) * TRACK_END_PERCENT)
      )
    );
  };

  const getJourneyProgress = (subtotal, rewards) => {
    if (!rewards.length) return 100;

    const highestThreshold = rewards[rewards.length - 1].threshold;
    if (highestThreshold <= 0) return 100;

    return Math.min(
      TRACK_END_PERCENT,
      Math.max(0, (subtotal / highestThreshold) * TRACK_END_PERCENT)
    );
  };

  const getRewardState = (cart, bar) => {
    const subtotal = Number(cart.total_price ?? cart.items_subtotal_price ?? 0);
    const currency = cart.currency || bar.dataset.currency || 'GBP';
    const defaultTitle = bar.dataset.defaultTitle || 'Added to your basket';
    const progressTemplate =
      bar.dataset.progressMessageTemplate || 'Add {amount} more to unlock {reward}';
    const rewards = getRewards(bar);
    const positions = getMilestonePositions(rewards);

    if (!rewards.length) {
      return {
        stateName: 'added',
        title: defaultTitle,
        message: 'Your item has been added successfully.',
        progress: 100,
        subtotal,
        currency,
        rewards,
        positions,
        nextReward: null,
      };
    }

    const unlocked = rewards.filter((reward) => subtotal >= reward.threshold);
    const locked = rewards.filter((reward) => subtotal < reward.threshold);
    const nextReward = locked[0] || null;
    const lastUnlocked = unlocked[unlocked.length - 1] || null;
    const progress = getJourneyProgress(subtotal, rewards);

    if (!nextReward) {
      return {
        stateName: 'unlocked',
        title: bar.dataset.allUnlockedTitle || 'All rewards unlocked!',
        message:
          bar.dataset.allUnlockedMessage || 'You’ve unlocked FREE delivery + 2 FREE gifts.',
        progress,
        subtotal,
        currency,
        rewards,
        positions,
        nextReward: null,
      };
    }

    const message = applyTemplate(progressTemplate, {
      amount: formatMoney(nextReward.threshold - subtotal, currency),
      reward: nextReward.label,
    });

    return {
      stateName: unlocked.length ? 'partial' : 'progress',
      title: lastUnlocked ? lastUnlocked.unlockedTitle : defaultTitle,
      message,
      progress,
      subtotal,
      currency,
      rewards,
      positions,
      nextReward,
    };
  };

  const getDefaultMilestoneIcon = (rewardKey) => {
    if (rewardKey === 'shipping') {
      return `
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
          <circle cx="7" cy="18" r="2" stroke="currentColor" stroke-width="1.8"/>
          <circle cx="18" cy="18" r="2" stroke="currentColor" stroke-width="1.8"/>
        </svg>
      `;
    }

    return `
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 9h16v11H4zM3 5h18v4H3zM12 5v15" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
        <path d="M12 5H8.8a2.3 2.3 0 1 1 2.3-2.3C11.1 4.3 12 5 12 5Zm0 0h3.2a2.3 2.3 0 1 0-2.3-2.3C12.9 4.3 12 5 12 5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
      </svg>
    `;
  };

  const setMilestoneIcon = (marker, reward) => {
    const iconWrap = marker.querySelector('.cae-aov-motivator__milestone-icon');
    if (!iconWrap) return;

    const currentCustomIcon = iconWrap.querySelector('img')?.getAttribute('src') || '';
    if (reward.icon && currentCustomIcon === reward.icon) return;
    if (!reward.icon && iconWrap.querySelector('svg')) return;

    if (reward.icon) {
      const image = document.createElement('img');
      image.src = reward.icon;
      image.alt = '';
      image.width = 15;
      image.height = 15;
      image.loading = 'eager';
      iconWrap.replaceChildren(image);
    } else {
      iconWrap.innerHTML = getDefaultMilestoneIcon(reward.key);
    }
  };

  const ensureMilestoneMarkup = (bar, rewards) => {
    const progressTrack = bar.querySelector('[data-aov-progress-track]');
    if (!progressTrack) return;

    rewards.forEach((reward) => {
      let marker = progressTrack.querySelector(
        `[data-aov-milestone][data-reward-key="${reward.key}"]`
      );

      if (!marker) {
        marker = document.createElement('span');
        marker.className = `cae-aov-motivator__milestone cae-aov-motivator__milestone--${
          reward.key === 'shipping' ? 'shipping' : 'gift'
        }`;
        marker.dataset.aovMilestone = '';
        marker.dataset.rewardKey = reward.key;
        marker.innerHTML = `
          <span class="cae-aov-motivator__milestone-icon" aria-hidden="true"></span>
          <span class="cae-aov-motivator__milestone-amount"></span>
        `;
        progressTrack.appendChild(marker);
      }

      marker.dataset.threshold = String(reward.threshold);
      marker.hidden = false;
      setMilestoneIcon(marker, reward);
    });
  };

  const updateMilestones = (bar, rewardState) => {
    ensureMilestoneMarkup(bar, rewardState.rewards);

    bar.querySelectorAll('[data-aov-milestone]').forEach((marker) => {
      const rewardKey = marker.dataset.rewardKey || '';
      const rewardIndex = rewardState.rewards.findIndex((reward) => reward.key === rewardKey);
      const reward = rewardState.rewards[rewardIndex];

      if (!reward || rewardIndex < 0) {
        marker.hidden = true;
        return;
      }

      marker.hidden = false;
      marker.style.left = `${rewardState.positions[rewardIndex]}%`;
      marker.dataset.threshold = String(reward.threshold);
      setMilestoneIcon(marker, reward);

      const reached = rewardState.subtotal >= reward.threshold;
      const isNext = rewardState.nextReward?.key === rewardKey;
      const amountNode = marker.querySelector('.cae-aov-motivator__milestone-amount');

      marker.classList.toggle('is-reached', reached);
      marker.classList.toggle('is-next', Boolean(isNext));

      if (amountNode) {
        amountNode.textContent = formatMoney(reward.threshold, rewardState.currency);
      }
    });
  };

  const requestJSON = (url, options = {}) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      xhr.onload = () => {
        let data;

        try {
          data = JSON.parse(xhr.responseText || '{}');
        } catch (error) {
          reject(new Error('The basket returned an invalid response.'));
          return;
        }

        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data,
        });
      };

      xhr.onerror = () => reject(new Error('The basket request could not be completed.'));
      xhr.onabort = () => reject(new Error('The basket request was cancelled.'));
      xhr.send(options.body || null);
    });

  const updateCartCount = (cart) => {
    const itemCount = Number(cart?.item_count || 0);
    const selectors = [
      '#cart-icon-bubble .cdo-header__cart-count',
      '#cart-icon-bubble [data-cart-count]',
      '[data-cart-count]',
      '.cart-count-bubble span:first-child',
    ];

    document.querySelectorAll(selectors.join(',')).forEach((node) => {
      node.textContent = String(itemCount);
    });
  };

  const hideMotivator = () => {
    const bar = getBar();
    if (!bar) return;

    bar.classList.remove('is-visible');
    bar.setAttribute('aria-hidden', 'true');

    window.clearTimeout(bar.hideAttributeTimer);
    bar.hideAttributeTimer = window.setTimeout(() => {
      if (!bar.classList.contains('is-visible')) bar.hidden = true;
    }, 240);
  };

  const showMotivator = (cart) => {
    const bar = getBar();
    if (!bar || !cart) return;

    const rewardState = getRewardState(cart, bar);
    const titleNode = bar.querySelector('[data-aov-title]');
    const messageNode = bar.querySelector('[data-aov-message]');
    const progressNode = bar.querySelector('[data-aov-progress]');
    const progressTrack = bar.querySelector('[data-aov-progress-track]');

    bar.dataset.rewardState = rewardState.stateName;
    bar.classList.toggle('is-unlocked', rewardState.stateName === 'unlocked');
    bar.classList.toggle('is-partial', rewardState.stateName === 'partial');

    if (titleNode) titleNode.textContent = rewardState.title;
    if (messageNode) messageNode.textContent = rewardState.message;
    if (progressNode) progressNode.style.width = `${rewardState.progress}%`;
    if (progressTrack) {
      progressTrack.setAttribute('aria-valuenow', String(Math.round(rewardState.progress)));
    }

    updateMilestones(bar, rewardState);

    window.clearTimeout(bar.hideAttributeTimer);
    bar.hidden = false;
    bar.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      bar.classList.add('is-visible');
    });
  };

  const fetchCart = async () => {
    const rootPath = window.Shopify?.routes?.root || '/';
    const cartUrl = `${rootPath.replace(/\/$/, '')}/cart.js`;
    const response = await requestJSON(cartUrl);

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.data;
  };

  const wait = (duration) =>
    new Promise((resolve) => window.setTimeout(resolve, Math.max(0, duration)));

  const refreshCartAndShow = async () => {
    const refreshId = ++state.rewardRefreshId;
    await wait(CART_SETTLE_MS);

    const cart = await fetchCart();
    if (refreshId !== state.rewardRefreshId) return cart;

    updateCartCount(cart);
    showMotivator(cart);
    return cart;
  };

  const getFormButtons = (form) =>
    Array.from(
      form.querySelectorAll(
        '[data-add-to-cart], [data-gift-add], button[type="submit"], input[type="submit"]'
      )
    );

  const getAjaxCollectionButton = (form) => {
    if (!form.closest('.cae-products')) return null;
    return form.querySelector('[data-add-to-cart]');
  };

  const getAjaxCollectionButtonText = (button) =>
    button?.querySelector('[data-add-to-cart-text], .btn__text');

  const restoreAjaxCollectionButton = (button) => {
    const text = getAjaxCollectionButtonText(button);
    if (!button || !text) return;

    window.clearTimeout(button.caeAddedTimer);
    button.removeAttribute('data-cae-added');

    if (button.dataset.caeOriginalText) {
      text.textContent = button.dataset.caeOriginalText;
    }

    if (button.dataset.caeOriginalAriaLabel) {
      button.setAttribute('aria-label', button.dataset.caeOriginalAriaLabel);
    } else {
      button.removeAttribute('aria-label');
    }
  };

  const showAjaxCollectionButtonAdded = (form) => {
    const button = getAjaxCollectionButton(form);
    const text = getAjaxCollectionButtonText(button);
    if (!button || !text) return;

    if (!button.dataset.caeOriginalText) {
      button.dataset.caeOriginalText = text.textContent.trim() || 'Add to cart';
      button.dataset.caeOriginalAriaLabel = button.getAttribute('aria-label') || '';
    }

    restoreAjaxCollectionButton(button);
    text.textContent = 'Added to basket';
    button.setAttribute('data-cae-added', 'true');
    button.setAttribute('aria-label', 'Added to basket');

    button.caeAddedTimer = window.setTimeout(() => {
      restoreAjaxCollectionButton(button);
    }, AJAX_BUTTON_ADDED_MS);
  };

  const setFormLoading = (form, loading) => {
    const ajaxButton = getAjaxCollectionButton(form);

    if (loading && ajaxButton) {
      const text = getAjaxCollectionButtonText(ajaxButton);
      if (text && !ajaxButton.dataset.caeOriginalText) {
        ajaxButton.dataset.caeOriginalText = text.textContent.trim() || 'Add to cart';
        ajaxButton.dataset.caeOriginalAriaLabel = ajaxButton.getAttribute('aria-label') || '';
      }
      restoreAjaxCollectionButton(ajaxButton);
    }

    getFormButtons(form).forEach((button) => {
      if (loading) {
        button.dataset.caeWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
      } else {
        if (button.dataset.caeWasDisabled !== 'true') button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        delete button.dataset.caeWasDisabled;
      }

      button.classList.toggle('product-form__cart-submit--loading', loading);
      button.classList.toggle('btn--loading', loading);
    });
  };

  const clearFormError = (form) => {
    const giftError = form.querySelector('[data-gift-error]');
    if (giftError) {
      giftError.hidden = true;
      giftError.textContent = '';
    }

    const wrapper = form
      .closest('product-form')
      ?.querySelector('.product-form__error-message-wrapper');
    wrapper?.classList.add('hidden');
  };

  const showFormError = (form, message) => {
    const giftError = form.querySelector('[data-gift-error]');
    if (giftError) {
      giftError.textContent = message;
      giftError.hidden = false;
      return;
    }

    const wrapper = form
      .closest('product-form')
      ?.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !text) return;

    text.textContent = message;
    wrapper.classList.remove('hidden');
  };

  const updateSuccessfulForm = (form) => {
    if (!form.matches('[data-gift-form]')) return;

    const button = form.querySelector('[data-gift-add]');
    const label = button?.querySelector('[data-gift-label]');
    form.closest('.cfg__card')?.setAttribute('data-added', 'true');
    if (label) label.textContent = button.dataset.addedLabel || 'Added';
  };

  const getCartAddUrl = () => {
    const configuredUrl = window.routes?.cart_add_url || '/cart/add';
    const [base, query = ''] = configuredUrl.split('?');
    const jsonBase = base.endsWith('.js') ? base : `${base}.js`;
    return query ? `${jsonBase}?${query}` : jsonBase;
  };

  const addCollectionProduct = async (form) => {
    if (state.submittingForms.has(form)) return;
    state.submittingForms.add(form);
    clearFormError(form);

    const ajaxButton = getAjaxCollectionButton(form);
    const loadingStartedAt = window.performance?.now?.() || Date.now();
    let addedSuccessfully = false;

    setFormLoading(form, true);

    try {
      const formData = new FormData(form);
      const variantId = formData.get('id');
      if (!variantId) throw new Error('Product variant is missing.');

      const quantity = Number(formData.get('quantity') || 1);
      formData.set('quantity', String(Number.isFinite(quantity) && quantity > 0 ? quantity : 1));
      formData.delete('sections');
      formData.delete('sections_url');

      const response = await requestJSON(getCartAddUrl(), {
        method: 'POST',
        body: formData,
      });
      const result = response.data;

      if (!response.ok || result.status) {
        throw new Error(
          result.description || result.message || 'Unable to add this item to the basket.'
        );
      }

      updateSuccessfulForm(form);
      await refreshCartAndShow();
      addedSuccessfully = true;
    } catch (error) {
      console.error('[Cardology collection add]', error);
      showFormError(form, error.message || 'Unable to add this item to the basket.');
    } finally {
      if (ajaxButton && addedSuccessfully) {
        const now = window.performance?.now?.() || Date.now();
        const elapsed = now - loadingStartedAt;
        if (elapsed < AJAX_BUTTON_MIN_LOADING_MS) {
          await wait(AJAX_BUTTON_MIN_LOADING_MS - elapsed);
        }
      }

      state.submittingForms.delete(form);
      setFormLoading(form, false);

      if (addedSuccessfully) {
        showAjaxCollectionButtonAdded(form);
      }
    }
  };

  const getTargetCartForm = (target) => {
    if (!(target instanceof Element)) return null;
    const form = target.matches('form') ? target : target.closest('form');
    if (!(form instanceof HTMLFormElement)) return null;
    if (!form.matches(CART_FORM_SELECTOR)) return null;
    return form;
  };

  const bindDirectCollectionAdds = () => {
    if (document.documentElement.dataset.caeDirectAddBound === 'true') return;
    document.documentElement.dataset.caeDirectAddBound = 'true';

    window.addEventListener(
      'click',
      (event) => {
        if (!isTargetCollectionPage()) return;
        if (!(event.target instanceof Element)) return;

        const submitControl = event.target.closest(
          '[data-add-to-cart], [data-gift-add], button[type="submit"], input[type="submit"]'
        );
        const form = getTargetCartForm(submitControl);
        if (!submitControl || !form || submitControl.disabled) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        addCollectionProduct(form);
      },
      true
    );

    window.addEventListener(
      'submit',
      (event) => {
        if (!isTargetCollectionPage()) return;
        const form = getTargetCartForm(event.target);
        if (!form) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        addCollectionProduct(form);
      },
      true
    );
  };

  const openBasket = () => {
    hideMotivator();

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { source: 'collection-aov-view-basket' },
      })
    );

    const cartTrigger = document.getElementById('cart-icon-bubble');
    if (cartTrigger) {
      cartTrigger.click();
      return;
    }

    window.location.assign(window.routes?.cart_url || '/cart');
  };

  const setupBar = () => {
    const bar = getBar();
    if (!bar || bar.dataset.caeAovReady === 'true') return;

    const rewards = getRewards(bar);
    ensureMilestoneMarkup(bar, rewards);

    bar.dataset.caeAovReady = 'true';
    bar.querySelector('[data-aov-close]')?.addEventListener('click', hideMotivator);
    bar.querySelector('[data-aov-view-basket]')?.addEventListener('click', openBasket);
  };

  const setup = () => {
    if (!isTargetCollectionPage()) return;
    setupBar();
    bindDirectCollectionAdds();
  };

  setup();
  document.addEventListener('shopify:section:load', setup);
  document.addEventListener('collection:updated', setup);
})();
